"use client";

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";

const CONTACT_REASONS = [
  { value: "sales", label: "Sales — pricing & plans" },
  { value: "enterprise", label: "Enterprise / multi-location" },
  { value: "partnership", label: "Partnership or reseller" },
  { value: "support", label: "Product support" },
  { value: "other", label: "Something else" },
] as const;

type ContactSalesModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ContactSalesModal({ open, onOpenChange }: ContactSalesModalProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [contactReason, setContactReason] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setContactReason("");
    setMessage("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactReason) {
      toast.error("Please select who you'd like to contact.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/contact-sales", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          phone,
          contactReason,
          message,
        }),
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) throw new Error(data.error ?? "Submission failed");

      toast.success("Thanks! Our team will be in touch shortly.");
      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[100] max-h-[min(90vh,100dvh)] max-w-lg overflow-y-auto overscroll-contain sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">Contact Sales</DialogTitle>
          <DialogDescription>
            Tell us a bit about your team and we&apos;ll be in touch shortly.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void handleSubmit(e)} className="mt-2 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="contact-sales-first-name">
                First name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="contact-sales-first-name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-sales-last-name">
                Last name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="contact-sales-last-name"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-sales-email">
              Work email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="contact-sales-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-sales-phone">
              Phone number <span className="text-destructive">*</span>
            </Label>
            <Input
              id="contact-sales-phone"
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>
              I want to contact <span className="text-destructive">*</span>
            </Label>
            <Select value={contactReason} onValueChange={setContactReason} required>
              <SelectTrigger>
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_REASONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-sales-message">Message (optional)</Label>
            <Textarea
              id="contact-sales-message"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us about your business and what you're looking for…"
            />
          </div>

          <Button type="submit" className="w-full sm:w-auto" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              "Contact Sales"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
